import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Ship, Plane, Check, ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { gsap, useGsap, prefersReducedMotion } from "@/lib/motion";

const MODES = [
  { key: "road", Icon: Truck },
  { key: "sea", Icon: Ship },
  { key: "air", Icon: Plane },
];
const CYCLE_MS = 6500;

/* Road, sea or air, side by side. The panel redraws its route and sends the
   vehicle along it each time a mode is chosen; while the section is in view
   (and the visitor hasn't picked one) it cycles on its own, with a progress
   bar on the active tab showing when it will move on. */
export default function ShippingModes() {
  const { t, lang } = useLang();
  const m = t.modesSection;
  const isRtl = lang === "ar";
  const root = useRef(null);
  const panel = useRef(null);
  const tabRefs = useRef([]);
  const [active, setActive] = useState(0);
  const [auto, setAuto] = useState(() => !prefersReducedMotion());
  const [inView, setInView] = useState(false);
  const [paused, setPaused] = useState(false);

  const mode = MODES[active];
  const ModeIcon = mode.Icon;
  const item = m.items[mode.key];

  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.35 });
    io.observe(root.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!auto || !inView || paused) return undefined;
    const id = setTimeout(() => setActive((a) => (a + 1) % MODES.length), CYCLE_MS);
    return () => clearTimeout(id);
  }, [auto, inView, paused, active]);

  const choose = (i, focus = false) => {
    setAuto(false);
    setActive(i);
    if (focus) tabRefs.current[i]?.focus();
  };

  const onKeyDown = (e) => {
    const next = { ArrowDown: 1, ArrowRight: isRtl ? -1 : 1, ArrowUp: -1, ArrowLeft: isRtl ? 1 : -1 }[e.key];
    if (next) {
      e.preventDefault();
      choose((active + next + MODES.length) % MODES.length, true);
    } else if (e.key === "Home") choose(0, true);
    else if (e.key === "End") choose(MODES.length - 1, true);
  };

  // Section entrance.
  useGsap(
    () => {
      gsap.from(".sm-reveal", {
        y: 30,
        opacity: 0,
        duration: 0.9,
        stagger: 0.08,
        scrollTrigger: { trigger: root.current, start: "top 75%", once: true },
      });
    },
    [lang],
    root,
  );

  // Every mode change: redraw the route, drive the vehicle, bring the copy in.
  useGsap(
    () => {
      const side = isRtl ? "right" : "left";
      const tl = gsap.timeline();
      tl.fromTo(".sm-line", { scaleX: 0 }, { scaleX: 1, duration: 1.1, ease: "expo.inOut" })
        .from(".sm-stop", { scale: 0, duration: 0.45, stagger: 0.12, ease: "power3.out" }, 0.15)
        .fromTo(".sm-vehicle", { [side]: "0%" }, { [side]: "100%", duration: 2.4, ease: "power1.inOut" }, 0.2)
        .from(".sm-copy > *", { y: 16, opacity: 0, filter: "blur(4px)", duration: 0.6, stagger: 0.06 }, 0.1);
    },
    [active, lang],
    panel,
  );

  return (
    <section
      ref={root}
      data-testid="modes-section"
      aria-labelledby="modes-title"
      className="bg-[var(--surface)] text-[var(--ink)] py-24 lg:py-36"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="container-x grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        <div className="lg:col-span-5">
          <p className="sm-reveal text-[15px] font-semibold text-[var(--accent-ink)]">{m.kicker}</p>
          <h2 id="modes-title" className="sm-reveal type-display mt-4 text-[clamp(2rem,3.8vw,3.3rem)] max-w-[14ch]">
            {m.title}
          </h2>
          <p className="sm-reveal mt-5 text-lg leading-relaxed text-[var(--muted-ink)] max-w-[44ch]">{m.lede}</p>

          <div role="tablist" aria-orientation="vertical" aria-label={m.kicker} onKeyDown={onKeyDown} className="sm-reveal mt-10 flex flex-col gap-2">
            {MODES.map(({ key, Icon }, i) => {
              const on = i === active;
              return (
                <button
                  key={key}
                  ref={(el) => {
                    tabRefs.current[i] = el;
                  }}
                  role="tab"
                  id={`mode-tab-${key}`}
                  aria-selected={on}
                  aria-controls="mode-panel"
                  tabIndex={on ? 0 : -1}
                  onClick={() => choose(i)}
                  className={`relative overflow-hidden flex items-center gap-4 rounded-2xl px-5 py-4 text-start transition-colors duration-300 ${
                    on ? "bg-[var(--forest-800)] text-[var(--on-dark)]" : "bg-[var(--bg)] hover:bg-[var(--line)]"
                  }`}
                >
                  <span
                    className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 transition-colors duration-300 ${
                      on ? "bg-[var(--signal)] text-[var(--signal-ink)]" : "bg-[var(--surface)] text-[var(--accent-ink)]"
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${isRtl ? "rtl-flip" : ""}`} aria-hidden />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-lg font-extrabold">{m.items[key].name}</span>
                    <span className={`block text-sm ${on ? "text-[var(--on-dark-muted)]" : "text-[var(--muted-ink)]"}`}>{m.items[key].transit}</span>
                  </span>
                  {on && auto && (
                    <span aria-hidden className="absolute bottom-0 inset-x-0 h-[3px] bg-white/10">
                      <span
                        key={active}
                        className="sm-progress block h-full bg-[var(--signal)] origin-left rtl:origin-right"
                        style={{
                          animationDuration: `${CYCLE_MS}ms`,
                          animationPlayState: paused || !inView ? "paused" : "running",
                        }}
                      />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div
          ref={panel}
          id="mode-panel"
          role="tabpanel"
          aria-labelledby={`mode-tab-${mode.key}`}
          className="sm-reveal lg:col-span-7 on-dark relative overflow-hidden rounded-[2rem] bg-[var(--forest-900)] p-6 sm:p-10 min-h-[440px]"
        >
          <div aria-hidden className="absolute inset-0 dot-grid opacity-50 -z-10" />

          {/* Route with stops and the travelling vehicle */}
          <div className="relative z-10">
            <div className="text-sm text-[var(--on-dark-muted)]">{m.labels.route}</div>
            <div className="relative mt-10 mb-4 mx-3 h-12">
              <div className="absolute inset-x-0 top-1/2 h-[3px] -mt-[1.5px] rounded-full bg-[repeating-linear-gradient(90deg,oklch(1_0_0/0.2)_0_8px,transparent_8px_14px)]" />
              <div className="sm-line absolute inset-x-0 top-1/2 h-[3px] -mt-[1.5px] rounded-full bg-[var(--signal)] origin-left rtl:origin-right" />
              {item.route.map((stop, i) => {
                const pos = item.route.length === 1 ? 0 : (i / (item.route.length - 1)) * 100;
                return (
                  <span key={stop} className="absolute top-1/2" style={{ insetInlineStart: `${pos}%` }}>
                    <span className={`sm-stop block w-3.5 h-3.5 -mt-[7px] ${isRtl ? "-mr-[7px]" : "-ml-[7px]"} rounded-full bg-[var(--signal)] ring-4 ring-[var(--forest-900)]`} />
                  </span>
                );
              })}
              <div className="sm-vehicle absolute top-1/2" style={{ [isRtl ? "right" : "left"]: "100%" }}>
                <div className={`-mt-[22px] ${isRtl ? "-mr-[22px]" : "-ml-[22px]"} w-11 h-11 rounded-xl grid place-items-center bg-[var(--on-dark)] text-[var(--forest-900)] shadow-[0_8px_24px_-8px_oklch(0_0_0/0.6)]`}>
                  <ModeIcon className={`w-5 h-5 ${isRtl ? "rtl-flip" : ""}`} aria-hidden />
                </div>
              </div>
            </div>
            <div className="flex justify-between gap-2 text-sm font-semibold text-[var(--on-dark)]">
              {item.route.map((stop) => (
                <span key={stop} className="max-w-[33%]">
                  {stop}
                </span>
              ))}
            </div>
          </div>

          <div className="sm-copy relative mt-12 grid sm:grid-cols-2 gap-8">
            <div>
              <div className="text-sm text-[var(--on-dark-muted)]">{m.labels.transit}</div>
              <div className="type-display mt-2 text-[clamp(2rem,4vw,3.25rem)] text-[var(--on-dark)]">{item.transit}</div>
            </div>
            <div>
              <div className="text-sm text-[var(--on-dark-muted)]">{m.labels.bestFor}</div>
              <ul className="mt-3 space-y-2.5">
                {item.best.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-[15px] text-[var(--on-dark)]">
                    <Check className="w-4 h-4 mt-1 shrink-0 text-[var(--signal)]" aria-hidden />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <Link to="/contact" className="sm:col-span-2 btn-primary h-12 px-6 rounded-full gap-2 justify-self-start">
              {m.labels.cta}
              <ArrowRight className="w-4 h-4 rtl-flip" aria-hidden />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
